import React, { useState, useEffect } from 'react';
import { Settings, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SlaSettings } from './SlaSettings';
import { SlaThreshold } from '@/types/pipeline';
import { SlidePanel } from '@/components/ui/slide-panel';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabaseClient';
import { getCurrentTenantId } from '@/utils/tenantUtils';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Plus, Pencil, Trash2, GripVertical, Shield, ArrowLeft } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided, DraggableProvided } from '@hello-pangea/dnd';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ChecklistAccessSelect } from '@/components/ui/checklist-access-select';

interface PipelineStagesManagerProps {
  pipelineId?: number;
  pipelineName: string;
  onStagesUpdated?: () => void;
  icon?: 'settings' | 'sun';
  variant?: 'icon' | 'button';
  // Template mode props
  templateMode?: boolean;
  templateId?: string;
  templateStages?: any[];
  onTemplateStagesUpdate?: (stages: any[]) => Promise<void>;
}

interface Stage {
  id: number | string;
  name: string;
  pipeline_id?: number;
  stage_order: number;
  sla_enabled: boolean;
  sla_settings: SlaThreshold[];
  permissions?: string[];
  user_permissions?: string[];
  description?: string;
}

interface RoleOption {
  label: string;
  value: string;
  description: string;
}

interface UserOption {
  label: string;
  value: string;
  email: string;
  role: string;
}

export function PipelineStagesManager({
  pipelineId,
  pipelineName,
  onStagesUpdated,
  icon = 'settings',
  variant = 'icon',
  templateMode = false,
  templateId,
  templateStages = [],
  onTemplateStagesUpdate
}: PipelineStagesManagerProps) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [stages, setStages] = useState<Stage[]>([]);
  const [hasStageChanges, setHasStageChanges] = useState(false);
  const [updatingStages, setUpdatingStages] = useState(false);

  // Stage form states
  const [viewMode, setViewMode] = useState<'list' | 'form' | 'delete-confirm'>('list');
  const [editingStage, setEditingStage] = useState<Stage | null>(null);
  const [deletingStage, setDeletingStage] = useState<Stage | null>(null);
  const [stageFormData, setStageFormData] = useState({ name: '', description: '' });
  const [slaEnabled, setSlaEnabled] = useState(false);
  const [slaSettings, setSlaSettings] = useState<SlaThreshold[]>([]);
  const [stageSelectedRoles, setStageSelectedRoles] = useState<string[]>([]);
  const [stageSelectedUsers, setStageSelectedUsers] = useState<string[]>([]);
  const [submittingStage, setSubmittingStage] = useState(false);

  // Permission options
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);

  // Fetch stages and data only when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (templateMode) {
        loadTemplateStages();
      } else {
        fetchStagesAndData();
      }
    }
  }, [isOpen, templateMode]);

  const loadTemplateStages = async () => {
    setIsLoading(true);
    try {
      // In template mode, use the templateStages prop
      setStages(templateStages || []);
      // Fetch permission options
      await loadPermissionOptions();
    } catch (error) {
      console.error('Error loading template stages:', error);
      toast({
        title: "Error",
        description: "Failed to load template stages",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStagesAndData = async () => {
    setIsLoading(true);
    try {
      // Fetch stages
      const { data: stagesData, error: stagesError } = await supabase
        .from('stages')
        .select('*')
        .eq('pipeline_id', pipelineId!)
        .order('stage_order', { ascending: true });

      if (stagesError) {
        throw stagesError;
      }

      setStages(stagesData || []);

      // Fetch permission options
      await loadPermissionOptions();

    } catch (error) {
      console.error('Error fetching stages:', error);
      toast({
        title: "Error",
        description: "Failed to load pipeline stages",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadPermissionOptions = async () => {
    try {
      const [rolesResponse, usersResponse] = await Promise.all([
        supabase
          .from('roles')
          .select('id, name, description')
          .order('name'),
        supabase
          .from('users')
          .select('id, first_name, last_name, email, roles(name)')
          .order('first_name', { ascending: true })
      ]);

      if (rolesResponse.data) {
        const formattedRoles: RoleOption[] = rolesResponse.data.map(role => ({
          label: role.name,
          value: role.name,
          description: `Allow users with ${role.name} role to access this stage`
        }));
        setRoleOptions(formattedRoles);
      }

      if (usersResponse.data) {
        const formattedUsers: UserOption[] = usersResponse.data.map(user => {
          const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
          const displayName = fullName || user.email;
          
          let userRole = '';
          if (user.roles) {
            if (Array.isArray(user.roles)) {
              userRole = user.roles[0]?.name || '';
            } else if (typeof user.roles === 'object') {
              userRole = (user.roles as any).name || '';
            }
          }

          return {
            label: displayName,
            value: user.id,
            email: user.email,
            role: userRole
          };
        });
        setUserOptions(formattedUsers);
      }
    } catch (error) {
      console.error('Error loading permission options:', error);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(stages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setStages(items);
    setHasStageChanges(true);
  };

  const handleUpdateStages = async () => {
    setUpdatingStages(true);
    try {
      if (templateMode) {
        // Update template stages in JSONB
        const updatedStages = stages.map((stage, index) => ({
          ...stage,
          stage_order: index + 1
        }));

        if (onTemplateStagesUpdate) {
          await onTemplateStagesUpdate(updatedStages);
        }

        setHasStageChanges(false);
        toast({
          title: "Success",
          description: "Template stage order updated successfully",
        });
      } else {
        // Always assign sequential stage_order to avoid conflicts
        const updates = stages.map((stage, index) => ({
          id: stage.id,
          name: stage.name,
          stage_order: index + 1,
          pipeline_id: stage.pipeline_id
        }));

        const tenantId = await getCurrentTenantId();

        // Step 1: Bump all stage_order values to negative range to avoid conflicts
        for (const update of updates) {
          const { error } = await supabase.from('stages').upsert([
            { ...update, stage_order: -(update.stage_order + 10000), tenant_id: tenantId }
          ]);
          if (error) {
            toast({
              title: "Error",
              description: `Failed to bump stage: ${update.name}`,
              variant: "destructive",
            });
            return;
          }
        }
        // Step 2: Set stage_order to the desired values
        for (const update of updates) {
          const { error } = await supabase.from('stages').upsert([
            { ...update, tenant_id: tenantId }
          ]);
          if (error) {
            toast({
              title: "Error",
              description: `Failed to update stage: ${update.name}`,
              variant: "destructive",
            });
            return;
          }
        }
        setHasStageChanges(false);
        toast({
          title: "Success",
          description: "Stage order updated successfully",
        });

        // Notify parent component that stages have been updated
        if (onStagesUpdated) {
          onStagesUpdated();
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update stage order",
        variant: "destructive",
      });
    } finally {
      setUpdatingStages(false);
    }
  };

  const handleClosePanel = () => {
    setIsOpen(false);
    setViewMode('list');
  };

  const handleBackToList = () => {
    setViewMode('list');
  };

  const handleOpenStageDialog = async (stage?: Stage) => {
    if (stage) {
      setEditingStage(stage);
      setStageFormData({ name: stage.name, description: stage.description || '' });
      setSlaEnabled(stage.sla_enabled || false);
      setSlaSettings(stage.sla_settings || []);
      setStageSelectedRoles(stage.permissions || []);
      setStageSelectedUsers(stage.user_permissions || []);
    } else {
      setEditingStage(null);
      setStageFormData({ name: '', description: '' });
      setSlaEnabled(false);
      setSlaSettings([]);
      setStageSelectedRoles([]);
      setStageSelectedUsers([]);
    }
    setViewMode('form');
  };

  const handleStageFormSubmit = async () => {
    if (!stageFormData.name.trim()) {
      toast({
        title: "Error",
        description: "Stage name is required",
        variant: "destructive",
      });
      return;
    }

    setSubmittingStage(true);
    try {
      if (templateMode) {
        // Template mode - update stages array
        let updatedStages: Stage[];

        if (editingStage) {
          // Update existing stage in template
          updatedStages = stages.map(stage =>
            stage.id === editingStage.id
              ? {
                  ...stage,
                  name: stageFormData.name,
                  description: stageFormData.description,
                  sla_enabled: slaEnabled,
                  sla_settings: slaSettings,
                  permissions: stageSelectedRoles,
                  user_permissions: stageSelectedUsers,
                }
              : stage
          );
        } else {
          // Create new stage in template
          const maxOrder = stages.length > 0 ? Math.max(...stages.map(s => s.stage_order)) : 0;
          const newStage: Stage = {
            id: `stage_${Date.now()}`,
            name: stageFormData.name,
            description: stageFormData.description,
            stage_order: maxOrder + 1,
            sla_enabled: slaEnabled,
            sla_settings: slaSettings,
            permissions: stageSelectedRoles,
            user_permissions: stageSelectedUsers,
          };
          updatedStages = [...stages, newStage];
        }

        if (onTemplateStagesUpdate) {
          await onTemplateStagesUpdate(updatedStages);
        }

        setStages(updatedStages);
        toast({
          title: "Success",
          description: `Template stage ${editingStage ? 'updated' : 'created'} successfully`,
        });
      } else {
        // Pipeline mode - database operations
        if (editingStage) {
          // Update existing stage
          const { error } = await supabase
            .from('stages')
            .update({
              name: stageFormData.name,
              sla_enabled: slaEnabled,
              sla_settings: slaSettings,
              permissions: stageSelectedRoles,
              user_permissions: stageSelectedUsers,
            })
            .eq('id', editingStage.id);

          if (error) throw error;

          toast({
            title: "Success",
            description: "Stage updated successfully",
          });
        } else {
          // Create new stage
          const maxOrder = stages.length > 0 ? Math.max(...stages.map(s => s.stage_order)) : 0;
          const tenantId = await getCurrentTenantId();

          const { error } = await supabase
            .from('stages')
            .insert({
              name: stageFormData.name,
              pipeline_id: pipelineId!,
              stage_order: maxOrder + 1,
              sla_enabled: slaEnabled,
              sla_settings: slaSettings,
              permissions: stageSelectedRoles,
              user_permissions: stageSelectedUsers,
              tenant_id: tenantId
            });

          if (error) throw error;

          toast({
            title: "Success",
            description: "Stage created successfully",
          });
        }

        fetchStagesAndData(); // Refresh the stages list

        // Notify parent component that stages have been updated
        if (onStagesUpdated) {
          onStagesUpdated();
        }
      }

      setViewMode('list');
    } catch (error) {
      console.error('Error saving stage:', error);
      toast({
        title: "Error",
        description: "Failed to save stage",
        variant: "destructive",
      });
    } finally {
      setSubmittingStage(false);
    }
  };

  const handleDeleteStageClick = (stage: Stage) => {
    // Show confirmation view
    setDeletingStage(stage);
    setViewMode('delete-confirm');
  };

  const confirmDeleteStage = async () => {
    if (!deletingStage) return;

    try {
      if (templateMode) {
        // Template mode - remove from stages array
        const updatedStages = stages.filter(s => s.id !== deletingStage.id);

        if (onTemplateStagesUpdate) {
          await onTemplateStagesUpdate(updatedStages);
        }

        setStages(updatedStages);
        toast({
          title: "Success",
          description: "Template stage deleted successfully",
        });
      } else {
        // Pipeline mode - database delete
        const { error } = await supabase
          .from('stages')
          .delete()
          .eq('id', deletingStage.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Stage deleted successfully",
        });

        fetchStagesAndData(); // Refresh the stages list

        // Notify parent component that stages have been updated
        if (onStagesUpdated) {
          onStagesUpdated();
        }
      }

      // Reset state
      setDeletingStage(null);
      setViewMode('list');
    } catch (error) {
      console.error('Error deleting stage:', error);
      toast({
        title: "Error",
        description: "Failed to delete stage",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      {variant === 'button' ? (
        <Button
          onClick={() => setIsOpen(true)}
          className=""
        >
          <GripVertical className="w-4 h-4 mr-2" />
          Manage Stages
        </Button>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(true)}
              className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200"
            >
              {icon === 'sun' ? (
                <Sun className="w-4 h-4" />
              ) : (
                <Settings className="w-4 h-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Manage Stages</p>
          </TooltipContent>
        </Tooltip>
      )}

      {/* Main Stages Management Dialog */}
      <SlidePanel
        isOpen={isOpen}
        onClose={handleClosePanel}
        title={
          viewMode === 'list' ? (
            `Manage Stages - ${pipelineName}`
          ) : viewMode === 'form' ? (
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToList}
                disabled={submittingStage}
                className="p-1 h-8 w-8"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <span className="text-lg font-semibold text-foreground">{editingStage ? 'Edit Stage' : 'Add Stage'}</span>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDeletingStage(null);
                  setViewMode('list');
                }}
                className="p-1 h-8 w-8"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <span className="text-lg font-semibold text-foreground">Delete Stage</span>
            </div>
          )
        }
        width="xl"
        topOffset="4.5rem"
        showEdgeCloseButton={true}
        footer={
          viewMode === 'list' ? (
            <div className="flex justify-end space-x-2">
              {hasStageChanges ? (
                <Button
                  onClick={handleUpdateStages}
                  disabled={updatingStages}
                >
                  {updatingStages ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating Stages...
                    </>
                  ) : (
                    'Update Stages'
                  )}
                </Button>
              ) : (
                <Button variant="outline" onClick={handleClosePanel}>Close</Button>
              )}
            </div>
          ) : viewMode === 'form' ? (
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={handleBackToList} disabled={submittingStage}>
                Cancel
              </Button>
              <Button onClick={handleStageFormSubmit} disabled={submittingStage}>
                {submittingStage ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {editingStage ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingStage ? 'Update Stage' : 'Add Stage'
                )}
              </Button>
            </div>
          ) : null // Delete confirmation has its own buttons in the content
        }
      >
        {viewMode === 'list' ? (
          /* List View */
          <>
            <div className="text-sm text-muted-foreground mb-6">
              Add, edit, delete, or reorder stages for this pipeline. Changes are saved automatically.
            </div>

            <div className="space-y-4">
              <div className="border rounded-lg bg-card">
                {isLoading ? (
                  <div className="flex items-center justify-center p-8">
                    <div className="flex items-center space-x-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm text-muted-foreground">Loading stages...</span>
                    </div>
                  </div>
                ) : (
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="stages">
                      {(provided: DroppableProvided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="min-h-[120px] w-full flex flex-col"
                        >
                          {stages.length === 0 ? (
                            <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground w-full">
                              <p className="mb-2">No stages added yet</p>
                              <p className="text-sm">Click "Add Stage" to create your first stage</p>
                            </div>
                          ) : (
                            stages.map((stage, index) => {
                              return (
                                <Draggable
                                  key={stage.id}
                                  draggableId={stage.id.toString()}
                                  index={index}
                                >
                                  {(provided: DraggableProvided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      className="flex items-center p-4 border-b last:border-b-0 bg-card hover:bg-muted w-full"
                                      style={{ width: '100%', ...provided.draggableProps.style }}
                                    >
                                      <div
                                        {...provided.dragHandleProps}
                                        className="mr-4 cursor-grab"
                                      >
                                        <GripVertical className="w-4 h-4 text-muted-foreground" />
                                      </div>
                                      <div className="flex-grow flex items-start justify-between min-h-[32px] py-1">
                                        <div className="flex-1 font-medium pr-3 flex items-center gap-2 text-foreground">
                                          {stage.name}
                                          {stage.sla_enabled && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
                                              SLA
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-3 flex-shrink-0">
                                          <div className="flex items-center space-x-2">
                                            <span className="text-sm text-muted-foreground whitespace-nowrap">
                                              {index + 1} of {stages.length}
                                            </span>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              onClick={() => handleOpenStageDialog(stage)}
                                              data-testid={`edit-stage-${stage.name}`}
                                            >
                                              <Pencil className="h-4 w-4" />
                                            </Button>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              onClick={() => handleDeleteStageClick(stage)}
                                              className="transition-opacity h-8 w-8 text-muted-foreground hover:text-destructive"
                                              title="Delete stage"
                                              data-testid={`delete-stage-${stage.name}`}
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </div>
              <div className="flex justify-end mt-2">
                <Button onClick={() => handleOpenStageDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Stage
                </Button>
              </div>
            </div>
          </>
        ) : viewMode === 'form' ? (
          /* Form View */
          <>
            <div className="text-sm text-muted-foreground mb-4">
              Enter a name for the stage and configure SLA settings and permissions.
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="stageName">Stage Name</Label>
                <Input
                  id="stageName"
                  value={stageFormData.name}
                  onChange={e => setStageFormData({ ...stageFormData, name: e.target.value })}
                  placeholder="Enter stage name"
                />
              </div>

              {templateMode && (
                <div className="space-y-2">
                  <Label htmlFor="stageDescription">Description (Optional)</Label>
                  <Textarea
                    id="stageDescription"
                    value={stageFormData.description}
                    onChange={e => setStageFormData({ ...stageFormData, description: e.target.value })}
                    placeholder="Enter stage description"
                    rows={2}
                  />
                </div>
              )}

              {/* SLA Tracking */}
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Switch
                    checked={slaEnabled}
                    onCheckedChange={(enabled) => {
                      if (enabled && slaSettings.length === 0) {
                        // Add default thresholds when enabling SLA
                        const defaultSettings: SlaThreshold[] = [
                          { days: 0, color: '#10b981', label: 'On Track' },
                          { days: 5, color: '#fbbf24', label: 'Warning' },
                          { days: 10, color: '#ef4444', label: 'Critical' },
                        ];
                        setSlaSettings(defaultSettings);
                      } else if (!enabled) {
                        // Clear SLA settings when disabling
                        setSlaSettings([]);
                      }
                      setSlaEnabled(enabled);
                    }}
                  />
                  <Label className="text-base font-medium">SLA Tracking</Label>
                </div>

                {slaEnabled && (
                  <SlaSettings
                    slaEnabled={slaEnabled}
                    onSlaEnabledChange={(enabled) => {
                      if (!enabled) {
                        // Only handle disabling - clearing the settings
                        setSlaSettings([]);
                        setSlaEnabled(false);
                      }
                    }}
                    slaSettings={slaSettings}
                    onSlaSettingsChange={setSlaSettings}
                  />
                )}
              </div>

              {/* Separator between SLA and Permissions */}
              <div className="border-t pt-4 mt-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" />
                    Stage Access Permissions
                  </Label>
                  <p className="text-sm text-muted-foreground mb-2 break-words whitespace-normal overflow-wrap-anywhere">
                    Control which roles and users can see this stage. If no permissions are set, the stage will be visible to all users.
                  </p>
                  <ChecklistAccessSelect
                    roleOptions={roleOptions}
                    userOptions={userOptions}
                    selectedRoles={stageSelectedRoles}
                    selectedUsers={stageSelectedUsers}
                    onRolesChange={setStageSelectedRoles}
                    onUsersChange={setStageSelectedUsers}
                    placeholder="Select roles and users..."
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          /* Delete Confirmation View */
          <>
            <div className="space-y-6">
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <h4 className="font-medium text-destructive mb-2">Confirm Deletion</h4>
                <p className="text-sm text-muted-foreground">
                  Are you sure you want to delete the stage <strong>"{deletingStage?.name}"</strong>
                  {templateMode ? ' from this template' : ''}? This action cannot be undone.
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeletingStage(null);
                    setViewMode('list');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={confirmDeleteStage}
                  data-testid="confirm-delete-stage"
                >
                  Delete Stage
                </Button>
              </div>
            </div>
          </>
        )}
      </SlidePanel>

    </>
  );
} 